import React, {useEffect, useState} from "react"
import {connect} from "react-redux"
import {CopyToClipboard} from "react-copy-to-clipboard"
import "pages/Styles/UIKit_v2.sass"
import "pages/Styles/Cells.sass"
import "pages/Styles/RequestClosingDocsPopup.sass"
import {httpClient} from "../../funcs";
import notifyUser, {notifyAboutError} from "../../methods/notifyUser";
import Popup from "../../components/Popup";
import CustomTextField from "../../components/elements/TextField";
import {CircularProgress, useMediaQuery} from "@mui/material";
import {validate} from "../../validate";
import {mobileMaxWidthMediaQuery} from "../../methods/utils";
import OrderInfo from "./OrderInfo";
import {ReactComponent as Copy} from "../Icons/size24/Copy.svg";
import UpdateUserFields from "../Profile/UpdateUserFields";

const OKIDOKI_EDO_OPERATOR = "ДИАДОК"
const OKIDOKI_EDO_PARTICIPANT_ID = "2BM-6330100935-633001001-202311010911486387630"

type Props = {
    close: Function,
    $store: any,
    $commitToStore: Function,
    bill: any,
    onSuccess?: Function
}

type PendingAction = null | 'download' | 'email' | 'edo'

type BuyerFieldConfig = {
    key: string
    label: string
    required?: boolean
}

// Эти поля мы показываем как превью реквизитов плательщика — они подтягиваются
// из профиля ($store.userSession) и редактируются через общий UpdateUserFields.
const BUYER_INFO_FIELDS: Array<BuyerFieldConfig> = [
    {key: 'law_name',    label: 'Наименование организации', required: true},
    {key: 'law_address', label: 'Юридический адрес',        required: true},
    {key: 'tax_id',      label: 'ИНН',                      required: true},
    {key: 'ogrn',        label: 'ОГРН / ОГРНИП'},
]

const BUYER_BANK_FIELDS: Array<BuyerFieldConfig> = [
    {key: 'bank_name',    label: 'Название банка'},
    {key: 'bank_account', label: 'Расчётный счёт'},
    {key: 'corr_account', label: 'Корр. счёт'},
    {key: 'bic',          label: 'БИК'},
]

const REQUIRED_BUYER_KEYS = [
    ...BUYER_INFO_FIELDS, ...BUYER_BANK_FIELDS,
].filter(f => f.required).map(f => f.key)

let RequestClosingDocsPopup: any = function RequestClosingDocsPopup(props: Props) {
    const isMobile = useMediaQuery(mobileMaxWidthMediaQuery)
    const userSession = props.$store.userSession || {}

    const [email, setEmail] = useState(userSession.email || "")
    const [emailError, setEmailError] = useState("")
    const [pendingAction, setPendingAction] = useState<PendingAction>(null)
    const [edoRequested, setEdoRequested] = useState(
        props.bill?.closing_document_status === "edo_requested"
    )

    useEffect(() => {
        if (email) {
            const error = validate('email', email)
            setEmailError(error || "")
        } else {
            setEmailError("")
        }
    }, [email])

    function getValue(key: string): string {
        const raw = userSession[key]
        return raw == null ? "" : String(raw).trim()
    }

    const taxIdValue = getValue('tax_id')
    const taxIdError = taxIdValue
        ? (validate('ruTaxId', taxIdValue, 'ИНН организации') || "")
        : ""

    const missingRequired = REQUIRED_BUYER_KEYS.filter(k => !getValue(k))
    const canSubmit = missingRequired.length === 0 && !taxIdError

    async function downloadAct() {
        try {
            setPendingAction('download')
            const response = await httpClient.post(
                '/closing-docs/download',
                {bill_id: props.bill._id},
                {responseType: 'blob'}
            )
            const blob = new Blob([response.data], {type: 'application/pdf'})
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `Акт_№${props.bill.invoice_number}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            notifyAboutError(props, error)
        } finally {
            setPendingAction(null)
        }
    }

    async function emailAct() {
        if (!email || emailError) return
        try {
            setPendingAction('email')
            await httpClient.post('/closing-docs/send-email', {
                bill_id: props.bill._id,
                email,
            })
            notifyUser(
                props,
                'Акт отправлен',
                'Копия отправлена вам на почту. Наш бухгалтер уже получил ваш запрос.'
            )
            if (props.onSuccess) props.onSuccess()
        } catch (error) {
            notifyAboutError(props, error)
        } finally {
            setPendingAction(null)
        }
    }

    async function requestEdo() {
        try {
            setPendingAction('edo')
            await httpClient.post('/closing-docs/request-edo', {bill_id: props.bill._id})
            setEdoRequested(true)
            if (props.onSuccess) props.onSuccess()
        } catch (error) {
            notifyAboutError(props, error)
        } finally {
            setPendingAction(null)
        }
    }

    function renderBuyerRow(f: BuyerFieldConfig) {
        const value = getValue(f.key)
        const isEmptyRequired = !!f.required && !value
        return (
            <div key={f.key} className="closing-docs-buyer-row">
                <div className="closing-docs-buyer-label">
                    {f.label}
                    {f.required && <span className="closing-docs-buyer-required"> *</span>}
                </div>
                <div
                    className={
                        "closing-docs-buyer-value" +
                        (isEmptyRequired ? " closing-docs-buyer-value--missing" : "") +
                        (!value ? " closing-docs-buyer-value--empty" : "")
                    }
                >
                    {value || 'Не указано'}
                </div>
            </div>
        )
    }

    return (
        <Popup onClose={props.close}>
            <div className="closing-docs-popup-content" style={{width: isMobile ? '100%' : '', maxWidth: '100%'}}>
                <div className="H3_21_v2 text-center">Закрывающие документы</div>

                <OrderInfo showTitle={true} cart={props.bill.items} />

                <div className="cell-card disabledHover closing-docs-buyer-cell">
                    <div className="cell-card-header-row">
                        <div className="cell-card-header-title">
                            <div className="cell-card-title">Данные плательщика</div>
                            <div className="cell-card-meta">Взяты из профиля</div>
                        </div>
                        <div className="cell-card-header-actions closing-docs-buyer-edit" title="Редактировать данные профиля">
                            <UpdateUserFields skipReload portal />
                        </div>
                    </div>

                    <div className="cell-card-content closing-docs-buyer-list">
                        {BUYER_INFO_FIELDS.map(renderBuyerRow)}
                        {BUYER_BANK_FIELDS.map(renderBuyerRow)}
                    </div>
                </div>

                {!!taxIdError && (
                    <div className="closing-docs-buyer-warning">
                        {taxIdError}
                    </div>
                )}
                {missingRequired.length > 0 && (
                    <div className="closing-docs-buyer-warning">
                        Заполните в профиле обязательные реквизиты: нажмите на
                        <span className="closing-docs-buyer-warning-icon"> ✎</span> рядом с заголовком.
                    </div>
                )}

                {/* Скачать / на почту */}
                <div className="closing-docs-section">
                    <div
                        className="Body_16_v2_secondary"
                        style={{marginTop: "20px", marginBottom: "10px"}}
                    >
                        Скачать акт или выслать на почту
                    </div>

                    <div className="closing-docs-actions">
                        <button
                            className="new action-button secondary"
                            disabled={!canSubmit || pendingAction !== null}
                            onClick={downloadAct}
                        >
                            <div className="buttonsContainer">
                                {pendingAction === 'download' && (
                                    <CircularProgress size={20} style={{color: '#333'}} />
                                )}
                                <div className="button-text">Скачать</div>
                            </div>
                        </button>
                    </div>

                    <div style={{marginTop: "16px"}}>
                        <div
                            className="Body_16_v2_secondary"
                            style={{marginBottom: "8px"}}
                        >
                            Или выслать на почту:
                        </div>
                        <CustomTextField
                            value={email}
                            error={!!emailError}
                            helperText={emailError}
                            onKeyPress={(e: any) => { if (e.key === 'Enter') e.preventDefault() }}
                            placeholder={"Введите email"}
                            onChange={({target: {value}}: any) => setEmail(value)}
                        />
                        <div className="flex-center" style={{marginTop: "12px"}}>
                            <button
                                className="new action-button primary"
                                disabled={!canSubmit || !email || !!emailError || pendingAction !== null}
                                onClick={emailAct}
                            >
                                <div className="buttonsContainer">
                                    {pendingAction === 'email' && (
                                        <CircularProgress size={20} style={{color: 'white'}} />
                                    )}
                                    <div className="button-text">Выслать на почту</div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ЭДО блок */}
                <div className="cell-card disabledHover closing-docs-edo-block">
                    <div className="cell-card-header-row">
                        <div className="cell-card-header-title">
                            <div className="cell-card-title">Получить по ЭДО</div>
                        </div>
                    </div>

                    <div className="cell-card-content">
                        {edoRequested && (
                            <div className="closing-docs-edo-done">
                                После установления связи в ЭДО закрывающие документы будут вам
                                отправлены. Наш бухгалтер уже получил ваш запрос.
                            </div>
                        )}

                        <div className="closing-docs-edo-hint">
                            {!edoRequested && (
                                <div className="closing-docs-edo-intro">
                                    Для получения документов через ЭДО отправьте нам приглашение:
                                </div>
                            )}
                            <div className="closing-docs-edo-creds">
                                <div><span className="label">Оператор ЭДО:</span> {OKIDOKI_EDO_OPERATOR}</div>
                                <div className="closing-docs-edo-guid-row">
                                    <div className="label">Идентификатор участника ЭДО (GUID):</div>
                                    <div className="closing-docs-edo-guid-value">
                                        <span className="guid">{OKIDOKI_EDO_PARTICIPANT_ID}</span>
                                        <CopyToClipboard
                                            text={OKIDOKI_EDO_PARTICIPANT_ID}
                                            onCopy={() => notifyUser(props, 'Идентификатор скопирован в буфер обмена')}
                                        >
                                            <button
                                                type="button"
                                                className="closing-docs-edo-copy-button"
                                                title="Скопировать идентификатор"
                                            >
                                                <Copy className="closing-docs-edo-copy-icon" aria-hidden />
                                            </button>
                                        </CopyToClipboard>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!edoRequested && (
                            <div className="flex-center" style={{marginTop: "12px"}}>
                                <button
                                    className="new action-button secondary"
                                    disabled={!canSubmit || pendingAction !== null}
                                    onClick={requestEdo}
                                >
                                    <div className="buttonsContainer">
                                        {pendingAction === 'edo' && (
                                            <CircularProgress size={20} style={{color: '#333'}} />
                                        )}
                                        <div className="button-text">Получить по ЭДО</div>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Popup>
    )
}

RequestClosingDocsPopup = connect(
    (store) => ({$store: store}),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({...data, type: 'S'})
    })
)(RequestClosingDocsPopup)

export default RequestClosingDocsPopup
